import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../hooks/useAuthContext'
import { useNavigate, Navigate, Link } from "react-router-dom";
import AuthPromptModal from '../../components/AuthPromptModal'
import AOS from 'aos';
import TypingAnimation from '../../components/TypingAnimation';
import FeaturesSection from '../../components/FeaturesSection';
import FeaturedGraphsSection from '../../components/FeaturedGraphsSection';
import Footer from '../../components/Footer';
import 'aos/dist/aos.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire, faStar, faCircle } from '@fortawesome/free-solid-svg-icons';
import { axiosInstance, publicAxios } from '../../context/AuthContext';
import logo from '../../components/logo.png';
import artizationLogo from '../../images/artization_logo.png';
import heroImage from '../../images/Art-Hero.jpeg';

function HomePage({ preview = false }) {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuthContext()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [artworks, setArtworks] = useState([])
  const [activeContentTab, setActiveContentTab] = useState("Overview");
  const [activeRightTab, setActiveRightTab] = useState("Trending");
  const [featuredImage, setFeaturedImage] = useState('');
  const [featuredId, setFeaturedId] = useState('');
  const [cardImages, setCardImages] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [latestNewsItem, setLatestNewsItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRoadmapLoading, setIsRoadmapLoading] = useState(true);
  const [isNewsLoading, setIsNewsLoading] = useState(true);
  const [featuredDesc, setFeaturedDesc] = useState("");
  const [artistName, setArtistName] = useState("");

  const isPreview = preview
  const isAuthenticatedAppUser =
    user && user.status === 'active' && user.permissions?.length > 0

  const requireAuth = () => setShowAuthModal(true)
  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' })
  }
  const scrollToExplore = () => scrollToSection('explore')
  const formattedDesc = featuredDesc ? featuredDesc.trim() : "";


  useEffect(() => {
    AOS.init({
      duration: 2000,
      offset: 100,
    });
  }, []);

  useEffect(() => {
    const restoreTopAfterFullscreen = () => {
      if (!document.fullscreenElement && window.scrollY > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    document.addEventListener('fullscreenchange', restoreTopAfterFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', restoreTopAfterFullscreen);
    };
  }, []);

  useEffect(() => {
    const fetchArtworks = async () => {
      if (isPreview) {
        setIsLoading(true);
        try {
          const response = await publicAxios.get('/artworks/preview?limit=12');
          const json = { artworks: response.data.artworks || [] };
          setArtworks(json.artworks);
          if (json.artworks.length > 2) {
            setFeaturedImage(json.artworks[2].imageBase64);
            setFeaturedId(json.artworks[2]._id);
          } else if (json.artworks.length > 0) {
            setFeaturedImage(json.artworks[0].imageBase64);
            setFeaturedId(json.artworks[0]._id);
          }
        } catch (error) {
          console.error("Error fetching preview artworks:", error);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (user) {
        setIsLoading(true);
        try {
          const response = await axiosInstance.get('/artworks?view=homepage');
          const json = response.data;
          setArtworks(json.artworks);
          if (json.artworks.length > 2) {
            setFeaturedImage(json.artworks[2].imageBase64);
            setFeaturedId(json.artworks[2]._id);
          } else if (json.artworks.length > 0) {
            setFeaturedImage(json.artworks[0].imageBase64);
            setFeaturedId(json.artworks[0]._id);
          }
        } catch (error) {
          console.error("Error fetching artworks for homepage:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchArtworks();
  }, [user, isPreview]);

  // Fetch roadmap items (always available to visitors)
  useEffect(() => {
    const fetchRoadmapItems = async () => {
      setIsRoadmapLoading(true);
      try {
        const response = await publicAxios.get('/settings/public/roadmap-items');
        setRoadmapItems(response.data || []);
      } catch (error) {
        console.error("Error fetching roadmap items:", error);
        if (user) {
          try {
            const fallback = await axiosInstance.get('/settings/roadmap-items');
            setRoadmapItems(fallback.data || []);
          } catch (fallbackError) {
            console.error("Fallback roadmap fetch failed:", fallbackError);
          }
        }
      } finally {
        setIsRoadmapLoading(false);
      }
    };

    fetchRoadmapItems();
  }, [user]);

  // Fetch latest published news item (always available to visitors)
  useEffect(() => {
    const fetchLatestNews = async () => {
      setIsNewsLoading(true);
      try {
        const response = await publicAxios.get('/settings/public/news-items/latest');
        if (response.data) {
          setLatestNewsItem(response.data);
        }
      } catch (error) {
        console.error("Error fetching latest news item:", error);
        if (user) {
          try {
            const fallback = await axiosInstance.get('/settings/news-items');
            const publishedNews = (fallback.data || []).filter(item => item.published);
            if (publishedNews.length > 0) {
              setLatestNewsItem(publishedNews[0]);
            }
          } catch (fallbackError) {
            console.error("Fallback news fetch failed:", fallbackError);
          }
        }
      } finally {
        setIsNewsLoading(false);
      }
    };

    fetchLatestNews();
  }, [user]);


  // Activate right tab

  useEffect(() => {
    if (artworks.length > 0) {
      const sortedArtworks = [...artworks].sort((a, b) => {
        return activeRightTab === "New" ? b._id.localeCompare(a._id) : a._id.localeCompare(b._id);
      });

      // Set featured artwork - prioritize the 3rd artwork if available, otherwise use first
      let featuredArtwork;
      if (artworks.length > 2) {
        featuredArtwork = artworks[2];
      } else {
        featuredArtwork = artworks[0];
      }

      setFeaturedImage(featuredArtwork.imageBase64);
      setFeaturedDesc(featuredArtwork.desc || ""); //set to empty string if no description exists.

      // Extract and format artist name
      const email = featuredArtwork.artist_email;
      const name = email.substring(0, email.indexOf("@"));
      const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
      setArtistName(formattedName);

      // Set card images dynamically based on available artworks
      const cardImagesArray = [];
      
      if (activeRightTab === "Trending") {
        for (let i = 0; i < 4; i++) {
          if (sortedArtworks.length > i) {
            cardImagesArray.push({
              url: sortedArtworks[i].imageBase64,
              title: sortedArtworks[i].title,
              id: sortedArtworks[i]._id
            });
          } else if (sortedArtworks.length > 0) {
            // If we don't have enough artworks, duplicate the last one
            const lastIndex = sortedArtworks.length - 1;
            cardImagesArray.push({
              url: sortedArtworks[lastIndex].imageBase64,
              title: sortedArtworks[lastIndex].title,
              id: sortedArtworks[lastIndex]._id
            });
          }
        }
      } else {
        if (sortedArtworks.length > 0) {
          cardImagesArray.push({
            url: sortedArtworks[0].imageBase64,
            title: sortedArtworks[0].title,
            id: sortedArtworks[0]._id
          });
        }
        
        if (sortedArtworks.length > 1) {
          cardImagesArray.push({
            url: sortedArtworks[1].imageBase64,
            title: sortedArtworks[1].title,
            id: sortedArtworks[1]._id
          });
        }
        
        if (sortedArtworks.length === 1) {
          cardImagesArray.push({
            url: sortedArtworks[0].imageBase64,
            title: sortedArtworks[0].title,
            id: sortedArtworks[0]._id
          });
        }
      }

      setCardImages(cardImagesArray);
    }
  }, [activeRightTab, artworks]);



  const handleCardClick = (id) => {
    if (isPreview) {
      requireAuth();
      return;
    }
    navigate(`/artwork/${id}`);
  };

  const handleProtectedNavigate = (path) => {
    if (isPreview) {
      requireAuth();
      return;
    }
    navigate(path);
  };

  const OverviewContent = () => (
    <>
      <div className='p-2'>
        <span className='headFont uppercase tracking-tight'> CYBERVISION </span> <br />
        <span className='headFont font-bold text-3xl sm:text-4xl lg:text-5xl uppercase'>Emotiology </span> <br />
        <span className='headFont font-bold text-xl sm:text-2xl uppercase tracking-widest'>Analytics</span>
      </div>
      <div className='p-2 self-end mb-8'>
        <span className='headFont font-bold uppercase tracking-wide'> Furture Of Exhibition</span>
        <p className='bodyFont text-sm'>
        Artization is an AI-powered analytics platform that helps artists and curators measure real-time viewer emotions and body language to understand true engagement with their work. 
        Turn every exhibition into actionable insights with smart dashboards, statistical data and visual graphs that reveal how people really feel about your art.
        </p>
      </div>
    </>
  )

  const RoadmapContent = () => (
    <div className="flex flex-col h-full p-2 pt-4 overflow-y-auto">
      <div className="mb-6">
        <span className='headFont uppercase tracking-tight'>Innovation Timeline</span>
        <h2 className='headFont font-bold text-2xl sm:text-4xl uppercase mt-2'>ROADMAP 2025</h2>
      </div>

      <div className="flex-1 relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {isRoadmapLoading ? (
          // Skeleton loading for roadmap items
          <div className="space-y-6">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="ml-8 relative">
                <div className="absolute -left-10 top-2">
                  <div className="w-3 h-3 bg-gray-200 rounded-full animate-pulse"></div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-20 mb-2"></div>
                  <div className="h-5 bg-gray-200 rounded animate-pulse w-32 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4 mt-1"></div>
                </div>
              </div>
            ))}
          </div>
        ) : roadmapItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No roadmap items available yet.</p>
          </div>
        ) : (
          roadmapItems.map((item, index) => (
            <div key={item._id} className="ml-8 mb-8 relative">
              <div className="absolute -left-10 top-2">
                <FontAwesomeIcon icon={faCircle} className="text-blue-500 text-sm" />
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition duration-200">
                {item.period && (
                  <span className="text-sm text-blue-500 font-semibold">{item.period}</span>
                )}
                <h3 className="font-bold mt-1">{item.subject}</h3>
                {item.description && (
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )

  const NewsContent = () => (
    <div className="flex flex-col h-full px-2 py-4">
      <div className="bg-white h-full overflow-y-auto">
        {isNewsLoading ? (
          // Skeleton loading for news content
          <div className="animate-pulse">
            <div className="text-center mb-4 border-b border-gray-200 pb-2">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-32 mx-auto"></div>
            </div>
            <div className="space-y-4">
              <div className="h-6 bg-gray-200 rounded w-2/3"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-4 border-b border-black pb-2">
              <h2 className='font-serif text-2xl sm:text-3xl font-bold tracking-tight'>{latestNewsItem?.title || 'Latest News'}</h2>
              <p className="text-[10px] mt-1">
                {latestNewsItem ? new Date(latestNewsItem.publishDate || latestNewsItem.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
              </p>
            </div>

            {latestNewsItem ? (
              <div className="space-y-2">
                <h3 className="font-serif text-xl font-bold leading-tight">{latestNewsItem.title}</h3>
                {latestNewsItem.author &&<p className="text-[12px] font-serif">By {latestNewsItem.author}</p>}

                <div 
                  className="font-serif text-[16px] leading-snug text-justify prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: latestNewsItem.content }}
                />

                <div className="text-center mt-4 pt-2 border-t border-gray-200">
                  <span className="font-serif text-[11px] italic">
                    {/* "Where Technology Meets Artistry" */}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="font-serif">No published news articles yet.</p>
                <p className="font-serif text-sm mt-2">Check back soon for the latest updates!</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )



  const renderContent = () => {
    switch (activeContentTab) {
      case "Overview":
        return <OverviewContent />;
      case "Roadmap":
        return <RoadmapContent />;
      case "News":
        return <NewsContent />;
      default:
        return <OverviewContent />;
    }
  };

  if (!authLoading && isPreview && isAuthenticatedAppUser) {
    return <Navigate to="/home" replace />
  }

  return (
    <div className="flex flex-col bg-paper text-gray-900">
      <header className="homepage-glass-nav sticky top-0 z-30 -mb-[56px] border-b border-white/40 text-gray-900 shadow-lg">
        <div className="mx-auto flex min-h-[56px] max-w-7xl items-center justify-between gap-6 px-5 sm:px-8">
          <a href="#top" className="flex shrink-0 items-center gap-3" aria-label="Artization home">
            <img src={artizationLogo} alt="Artization" className="h-8 w-auto" />
          </a>

          <nav className="hidden items-center gap-2 ml-28 text-xs font-semibold uppercase tracking-[0.14em] md:flex headFont" aria-label="Landing page navigation">
            <button type="button" onClick={() => scrollToSection('top')} className="rounded-full px-3 py-1.5 transition-colors duration-200 hover:bg-white/30 hover:text-gray-900">Home</button>
            <button type="button" onClick={() => scrollToSection('explore')} className="rounded-full px-3 py-1.5 transition-colors duration-200 hover:bg-white/30 hover:text-gray-900">Explore</button>
            <button type="button" onClick={() => scrollToSection('analytics')} className="rounded-full px-3 py-1.5 transition-colors duration-200 hover:bg-white/30 hover:text-gray-900">Analytics</button>
            <button type="button" onClick={() => scrollToSection('features')} className="rounded-full px-3 py-1.5 transition-colors duration-200 hover:bg-white/30 hover:text-gray-900">Features</button>
          </nav>

          

          <div className="flex shrink-0 items-center gap-3 text-sm">
            {user ? (
              <>
                <Link
                  to="/user-settings"
                  className="rounded-full px-4 py-2 transition duration-200 bg-black/10 hover:bg-black/15 text-gray-800 hover:text-gray-900 backdrop-blur-md"
                  style={{boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'}}
                >
                  {user.firstName || 'Account'}
                </Link>
                <Link
                  to="/artozphere"
                  className="rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 px-4 py-2 font-semibold text-black transition duration-200 hover:from-yellow-500 hover:to-yellow-600 shadow-md hover:shadow-lg"
                >
                  Artozphere
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="rounded-full px-4 py-2 transition duration-200 bg-black/10 hover:bg-white/30 text-gray-800 hover:text-gray-900 backdrop-blur-md"
                  style={{boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'}}
                >
                  Log in
                </Link>
                <button
                  type="button"
                  onClick={scrollToExplore}
                  className="rounded-full px-4 py-2 font-semibold text-white transition duration-200 bg-black/60 hover:bg-white/30 hover:text-gray-900 backdrop-blur-md"
                  style={{boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'}}
                >
                  Try Artization
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <section id="top" className="relative flex min-h-[min(650px,86svh)] flex-col items-center justify-start overflow-hidden bg-black text-white">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/70" aria-hidden="true" />
        <div className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-5 pb-24 pt-24 text-center sm:flex-none sm:justify-start sm:px-8 sm:pb-28 sm:pt-28">
          <h1 className="max-w-4xl text-5xl font-semibold leading-[0.96] tracking-tight text-slate-800 sm:text-7xl lg:text-7xl">What does <span className='text-emerald-600'>ART</span> make you feel?</h1>
          <button type="button" onClick={scrollToExplore} className="mt-9 border-4 border-black bg-neutral-100 px-7 py-3 text-md font-semibold text-zinc-600 transition hover:bg-yellow-400">Explore Artization <span aria-hidden="true">↓</span></button>
        </div>
        <div className="absolute inset-x-0 bottom-0 border-t border-none py-5 text-white/90 backdrop-blur-[2px] bg-gradient-to-t from-black/50 via-black/25 to-transparent" aria-label="Featured locations">
          <div className="mb-4 text-center text-lg font-medium tracking-[0.15em] text-white/65">Coming To</div>
          <div className="overflow-hidden relative">
            <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-black/25 to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-black/25 to-transparent z-10 pointer-events-none" />
            <div className="sponsor-marquee flex min-w-max items-center gap-24 px-8 text-xl font-semibold sm:gap-32 sm:text-2xl">
              <span className="flex-shrink-0">Sydney</span>
              <span className="flex-shrink-0">Melbourne</span>
              <span className="flex-shrink-0">Hobart</span>
              <span className="flex-shrink-0">Canberra</span>
              <span className="flex-shrink-0">Newcastle</span>
              <span className="flex-shrink-0" aria-hidden="true">Sydney</span>
              <span className="flex-shrink-0" aria-hidden="true">Melbourne</span>
              <span className="flex-shrink-0" aria-hidden="true">Hobart</span>
              <span className="flex-shrink-0" aria-hidden="true">Canberra</span>
              <span className="flex-shrink-0" aria-hidden="true">Newcastle</span>
            </div>
          </div>
        </div>
      </section>

      <div className="flex-1 flex flex-col ml-0 px-3 sm:px-4 lg:px-0">
        <div id="explore" className="flex items-center justify-center w-full rounded sm:shadow-md pt-10 md:pt-8 scroll-mt-4">
          <div className="mx-0 sm:mx-4 grid gap-6 lg:gap-4 grid-cols-1 h-90vh lg:grid-cols-12 w-full h-auto justify-center">
            <div className='col-span-12 lg:col-span-3 flex flex-col gap-4 lg:justify-between lg:min-h-0 order-2 lg:order-1'>
              {/* Left Navigation Menu */}
              <div className="w-auto flex justify-between text-xs sm:text-sm mt-2 lg:mt-4">
                <ul className="flex w-full gap-x-1 sm:gap-x-2 px-1 py-1 font-semibold border rounded-full border-gray-400 bg-gray-100 shadow-md">
                  {["Overview", "Roadmap", "News"].map((tab) => (
                    <li
                      key={tab}
                      className={`cursor-pointer px-2 py-2 sm:px-4 rounded-full transition-all flex-1 text-center
                      ${activeContentTab === tab ? "bg-white shadow font-bold" : "text-gray-600 hover:text-gray-500"}`}
                      onClick={() => setActiveContentTab(tab)}
                    >
                      {tab}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Left Content */}
              {renderContent()}
            </div>

            {/* Featured Artwork Section */}
            <div
              className="col-span-12 lg:col-span-5 order-1 lg:order-2 ml-0 lg:ml-8 bg-cover bg-center rounded-xl w-full min-h-[42vh] sm:min-h-[48vh] lg:h-[90vh] relative group overflow-hidden"
              style={{ backgroundImage: featuredImage ? `url(${featuredImage})` : 'none' }}
            >
              {/* Show skeleton loading when loading */}
              {isLoading && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-xl">
                  <div className="absolute bottom-5 left-5 p-4 bg-gray-300 rounded-lg w-11/12 h-24 animate-pulse"></div>
                </div>
              )}

              {/* Show fallback when no artworks are available and not loading */}
              {!isLoading && !featuredImage && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="text-6xl mb-4">🎨</div>
                    <h2 className="text-2xl font-bold text-gray-600 mb-2">No Artworks Yet or Contact Support</h2>
                    <p className="text-gray-500 mb-4">Start creating your gallery by adding your first artwork</p>
                    <button 
                      onClick={() => handleProtectedNavigate('/editor')}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                      Add Artwork
                    </button>
                  </div>
                </div>
              )}

              {/* Background Overlay (Appears on Hover) - only show if there's an image and not loading */}
              {!isLoading && featuredImage && <div onClick={() => handleCardClick(featuredId)} className="absolute cursor-pointer inset-0 bg-black opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>}

              {/* Description with Typing Animation - only show if there's an image and not loading */}
              {!isLoading && featuredImage && (
                <div className="absolute bottom-5 left-5 p-4 bg-black/70 text-white text-base sm:text-lg font-mono w-11/12 rounded-lg opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-500">
                  <TypingAnimation text={formattedDesc} speed={80} chunkSize={3} />
                  <p className="mt-2 text-sm">Artist: {artistName}</p> {/* Display artist name */}
                </div>
              )}
            </div>


            {/* Right Section */}
            <div className="col-span-12 lg:col-span-4 relative flex flex-col min-h-0 lg:h-[calc(100dvh-4rem)] order-3 pb-4 lg:pb-0">
              {/* Right Navigation Menu */}
              <div className="flex justify-center w-full min-h-12 mt-2 lg:mt-4">
                <ul className="flex flex-wrap justify-center gap-2 sm:gap-4">
                  {[
                    { name: "Trending", icon: faFire, hoverColor: "red" },
                    { name: "New", icon: faStar, hoverColor: "yellow" }
                  ].map((tab) => (
                    <li key={tab.name}>
                      <button
                        className={`flex items-center px-3 py-2 sm:px-4 rounded-full shadow-md text-sm sm:text-base transition group hover:bg-stone-100
            ${activeRightTab === tab.name ? "bg-stone-100" : ""}`}
                        onClick={() => setActiveRightTab(tab.name)}
                      >
                        <FontAwesomeIcon
                          icon={tab.icon}
                          className={`mr-2 transition-colors ${activeRightTab === tab.name
                            ? tab.name === "Trending"
                              ? "text-red-500"
                              : "text-yellow-500"
                            : "text-gray-600 group-hover:" + (tab.name === "Trending" ? "text-red-400" : "text-yellow-400")
                            }`}
                        />

                        {tab.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`relative lg:absolute lg:top-1/2 lg:left-4 lg:right-4 lg:-translate-y-1/2 w-full px-1 sm:px-2 lg:px-0 mt-4 lg:mt-0 ${activeRightTab === "Trending" ? "grid grid-cols-2 gap-2 sm:gap-4 justify-items-stretch sm:justify-items-center" : "flex flex-col sm:flex-row sm:flex-wrap justify-center items-stretch sm:items-center gap-4 sm:gap-6"}`}>
                {isLoading ? Array.from({ length: activeRightTab === "Trending" ? 4 : 2 }, (_, index) => (
                      <div key={index} className="w-full max-w-[13rem] sm:w-52 mx-auto h-52 sm:h-60 bg-gray-200 rounded-2xl shadow-md flex flex-col justify-between p-2 animate-pulse">
                        <div className="w-full h-3/4 bg-gray-300 rounded-lg animate-pulse"></div>
                        <div className="flex flex-row items-center justify-between w-full px-2">
                          <div className="h-4 bg-gray-300 rounded w-20 animate-pulse"></div>
                          <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    )
                ) : cardImages.length > 0 ? (
                  cardImages.map((card, index) => (
                    <div key={index} className="w-full max-w-[13rem] sm:w-52 mx-auto h-52 sm:h-60 bg-gray-100 rounded-2xl shadow-md flex flex-col justify-between p-2">
                      <div
                        className="w-full h-3/4 bg-cover bg-center rounded-lg cursor-pointer"
                        style={{ backgroundImage: `url(${card.url})` }}
                        onClick={() => handleCardClick(card.id)}
                      ></div>
                      <div className="flex flex-row items-center justify-between w-full px-2">
                        <span className="headFont font-bold text-sm uppercase">{card.title}</span>
                        <button
                          className="w-10 h-10 flex items-center justify-center bg-black text-white rounded-full"
                          onClick={() => handleCardClick(card.id)}
                        >
                          →
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={activeRightTab === "Trending" ? "grid grid-cols-2 gap-2 sm:gap-4 w-full" : "flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center w-full"}>
                    {Array.from({ length: activeRightTab === "Trending" ? 4 : 2 }, (_, index) => (
                      <div key={index} className="w-full max-w-[13rem] sm:w-52 mx-auto h-52 sm:h-60 bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center">
                        <div className="text-center text-gray-400">
                          <div className="text-4xl mb-2">🖼️</div>
                          <p className="text-sm">No artworks</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>


            </div>
          </div>
        </div>

        <div id="analytics" className="scroll-mt-8">
          <FeaturedGraphsSection preview={isPreview} onAuthRequired={requireAuth} />
        </div>

        <div id="features" className="scroll-mt-8">
          <FeaturesSection preview={isPreview} onAuthRequired={requireAuth} />
        </div>

      </div>
      <Footer />
      <AuthPromptModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  )
}

export default HomePage